import React, {FC, useEffect, useRef, useState} from 'react'
import { useLazyGetProductQuery } from '../../features/products/productsApiSlice'
import { useParams } from 'react-router-dom'
import { IProduct, ObjectOfArrays } from '../../interfaces'
import { backURL } from '../../consts'
import CrossReference from '../../components/CrossReference/CrossReference'
import HeatRangeChart from '../../components/HeatRangeChart/HeatRangeChart'
import styles from './products.module.css'
import { TbCurrencyDram } from 'react-icons/tb'
import { useAppSelector } from '../../hooks/redux'
import CartActions from '../../components/CartActions/CartActions'
import { useTranslation } from 'react-i18next'
import i18next from "i18next";
import useLangChange, {IPropsToTranslate} from "../../hooks/useLangChange";
import Modal from "../../components/Modal/Modal";
import loadingImg from "../../imgs/loading.gif"
import {useCheckIdentityMutation} from "../../features/topSellers/topSellersApiSlice";
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { Helmet } from 'react-helmet';
import Breadcrumbs from '../../components/Breadcrumbs/Breadcrumbs';

// Shown when a product image fails to load or is missing
const NO_PHOTO = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'%3E%3Crect width='100' height='100' fill='%23f0f0f0'/%3E%3C/svg%3E"

const Product: FC = () => {
    // const params = useParams<{product_id: string}>()
    // const product_id = params.product_id
    const [checkIdentity] = useCheckIdentityMutation()
    const productContainerRef = useRef<HTMLDivElement>(null)
    const [modalImg, setModalImg] = useState('')
    const [modalOpen, setModalOpen] = useState(false)
    const { t } = useTranslation()
    const [lng, setLng] = useState('')
    const [product, setProduct] = useState<IProduct>({} as IProduct)
    const { cart } = useAppSelector((state) => state)
    const params = useParams()
    const { category: category_name } = params
    const [productCount_, setProductCount] = useState<{
        original: number
        copy: number
    }>({ original: 0, copy: 0 })
    const [productType, setProductType] = useState('original')
    const [focusedImg, setFocusedImg] = useState<null | number>(null)
    const [mainImgIndex, setMainImgIndex] = useState<null | number>(null)
    const [getProduct, { isLoading, isSuccess }] = useLazyGetProductQuery()
    const { category, product_id } = useParams()
    const [img, setImg] = useState('')
    const [refs, setRefs] = useState<ObjectOfArrays>({})
    const [oems, setOems] = useState<ObjectOfArrays>({})
    const [refsOpen, setRefsOpen] = useState(false)
    const [oemsOpen, setOemsOpen] = useState(false)
    const discountedOriginalPrice =
        product.discount !== 0
            ? product.price_original -
              (product.price_original * product.discount) / 100
            : product.price_original
    const discountedCopyPrice =
        product.discount !== 0
            ? product.price_copy - (product.price_copy * product.discount) / 100
            : product.price_copy

    const handleMainImgClick = (imgIndex: number) => {
        setFocusedImg(imgIndex)
        setMainImgIndex(imgIndex)
    }

    useEffect(() => {
        const handler = (lng: string) => setLng(lng)
        i18next.on('languageChanged', handler)
        return () => { i18next.off('languageChanged', handler) }
    }, [])

    const translatedProps: IPropsToTranslate = useLangChange(product, lng)

    const handleSetModalImg = (img: string) => {
        setModalImg(img)
        setModalOpen(true)
    }

    useEffect(() => {
        if (productContainerRef.current) {
            productContainerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }

        async function getIPData(){
            try{
                if(product_id){
                    const svechaUserIdentityLS = localStorage.getItem('svechaUserIdentityLS') || ''
                    const identityData = await checkIdentity({product_id, ls: svechaUserIdentityLS}).unwrap()
                    if(identityData.ls){
                        localStorage.setItem('svechaUserIdentityLS', identityData.ls)
                    }
                }
            }catch{
            }
        }
        getIPData()
    }, []);

    // Наверх при смене товара (в т.ч. клик по аналогу из Cross Reference),
    // чтобы клиент сразу видел выбранный товар, а не остался внизу у списка.
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [product_id]);

    useEffect(() => {
        getProduct(`/${category}/${product_id}`)
            .unwrap()
            .then((res) => {
                setProduct(res)
                const productRefs: { [key: string]: Array<string> } = {}
                const productOems: { [key: string]: Array<string> } = {}
                res.refs.forEach((refItem) => {
                    if (productRefs[refItem.brand]) {
                        productRefs[refItem.brand].push(refItem.ref_num)
                    } else {
                        productRefs[refItem.brand] = [refItem.ref_num]
                    }
                })
                setRefs(productRefs)
                res.oems.forEach((oemItem) => {
                    if (productOems[oemItem.model]) {
                        productOems[oemItem.model].push(oemItem.oem)
                    } else {
                        productOems[oemItem.model] = [oemItem.oem]
                    }
                })
                setOems(productOems)
            })
    }, [category, product_id])

    useEffect(() => {
        let imgToSet = product?.imgs?.find((imgItem) => imgItem.master)?.img
        if (imgToSet) {
            setImg(imgToSet)
        }
    }, [product])

    useEffect(() => {
        if (product) {
            const cartProduct = cart.products.find(
                (pr) => pr.product_id === product.product_id
            )
            if (productType === 'original') {
                setProductCount({
                    ...productCount_,
                    copy: cartProduct?.count_copy || 0,
                })
            } else if (productType === 'copy') {
                setProductCount({
                    ...productCount_,
                    original: cartProduct?.count_original || 0,
                })
            }
        }
    }, [productType, product])

    if (isLoading) {
        return <div className={styles.loading}><img src={loadingImg} /></div>
    }

    // ── SEO: уникальные title/description + Schema.org Product (JSON-LD) ──
    const categoryLabel = product?.category_name ? t(`categories.${product.category_name}`) : ''
    const masterImg = product?.imgs?.find((i) => i.master)?.img
    const seoImage = masterImg ? (masterImg.startsWith('http') ? masterImg : `${backURL}/${masterImg}`) : ''
    const seoTitle = `${product?.brand ?? ''} ${product?.model ?? ''} — ${categoryLabel} | Svecha.am`.replace(/\s+/g, ' ').trim()
    const seoDesc = `${product?.brand ?? ''} ${product?.model ?? ''}${product?.detail_number ? ` (${product.detail_number})` : ''} — ${categoryLabel}. Svecha.am`.replace(/\s+/g, ' ').trim()
    const pageUrl = `https://svecha.am/products/${category}/${product_id}`
    const inStock = (product?.count_original ?? 0) > 0 || (product?.count_copy ?? 0) > 0
    const seoPrice = product?.price_original || product?.price_copy || 0
    const productJsonLd = {
        '@context': 'https://schema.org/',
        '@type': 'Product',
        name: `${product?.brand ?? ''} ${product?.model ?? ''}`.trim(),
        ...(product?.brand ? { brand: { '@type': 'Brand', name: product.brand } } : {}),
        ...(product?.detail_number ? { mpn: String(product.detail_number) } : {}),
        ...(categoryLabel ? { category: categoryLabel } : {}),
        ...(seoImage ? { image: seoImage } : {}),
        offers: {
            '@type': 'Offer',
            priceCurrency: 'AMD',
            price: String(seoPrice),
            availability: inStock ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
            url: pageUrl,
        },
    }

    return isSuccess && product?.product_id ? (
        <div
            className={`${styles.product_wrapper} app_container`}
            style={{ marginLeft: 'auto', marginRight: 'auto' }}
        >
            <Helmet>
                <title>{seoTitle}</title>
                <meta name="description" content={seoDesc} />
                <meta property="og:title" content={seoTitle} />
                <meta property="og:description" content={seoDesc} />
                {seoImage ? <meta property="og:image" content={seoImage} /> : null}
                <meta property="og:url" content={pageUrl} />
                <link rel="canonical" href={pageUrl} />
                <script type="application/ld+json">{JSON.stringify(productJsonLd)}</script>
            </Helmet>
            <Modal open={modalOpen} maxWidth={700} onClose={() => setModalOpen(false)} >
                <div className={styles.modalImg}><div className={styles.modal_img_container}><img src={modalImg} /></div></div>
            </Modal>
            <Breadcrumbs
                items={[
                    { name: t('menu.main') || '', url: '/' },
                    { name: categoryLabel || '', url: `/products/${category}` },
                    { name: `${product.brand ?? ''} ${product.model ?? ''}`.trim() },
                ]}
            />
            <div className={styles.product_container} ref={productContainerRef}>
                <div className={styles.imgs}>
                    <div className={styles.side_imgs}>
                        {product.imgs.map((imgItem, index) => (
                            <img
                                key={imgItem.img}
                                className={
                                    focusedImg !== null && focusedImg === index
                                        ? styles.focused
                                        : ''
                                }
                                onClick={() => handleMainImgClick(index)}
                                src={`${backURL}/${imgItem.img}`}
                                alt={`${product.brand} ${product.model} — ${index + 1}`}
                                onError={(e) => { (e.target as HTMLImageElement).src = NO_PHOTO; }}
                            />
                        ))}
                    </div>
                    <div className={styles.main_img_container} >
                        {(() => {
                            const mainSrc = mainImgIndex !== null
                                ? `${backURL}/${product.imgs[mainImgIndex].img}`
                                : img
                                ? `${backURL}/${img}`
                                : NO_PHOTO;
                            return (
                                <img
                                    src={mainSrc}
                                    alt={`${product.brand} ${product.model}`}
                                    onClick={() => handleSetModalImg(mainSrc)}
                                    onError={(e) => { (e.target as HTMLImageElement).src = NO_PHOTO; }}
                                />
                            );
                        })()}
                    </div>
                </div>
                <div className={styles.about_container}>
                    <h1 className={styles.product_title}>{product.brand} {product.model}</h1>
                    <p className={styles.product_subtitle}>{t(`categories.${category_name}`)}</p>
                    <p className={styles.about}>
                        {t('product_features.about_product')}
                    </p>
                    <p className={styles.product_feature}>
                        <span className={styles.feature_title}>{t('product_features.brand')}</span>
                        <span className={`${styles.feature_value} ${styles.uc}`}>{product.brand}</span>
                    </p>
                    <p className={styles.product_feature}>
                        <span className={styles.feature_title}>{t('product_features.detail_number')}</span>
                        <span className={`${styles.feature_value} ${styles.uc}`}>{product.detail_number}</span>
                    </p>
                    <p className={styles.product_feature}>
                        <span className={styles.feature_title}>{t('product_features.model')}</span>
                        <span className={`${styles.feature_value} ${styles.uc}`}>{product.model}</span>
                    </p>
                    {category_name === 'ignition_coils' && (
                        <>
                            <p className={styles.product_feature}>
                                <span className={styles.feature_title}>{t('product_features.plugs_number')}</span>
                                <span className={styles.feature_value}>{product.plugs_number}</span>
                            </p>
                            <p className={styles.product_feature}>
                                <span className={styles.feature_title}>{t('product_features.contacts_number')}</span>
                                <span className={styles.feature_value}>{product.contacts_number}</span>
                            </p>
                        </>
                    )}
                    {category_name === 'ignition_coil_mouthpieces' && (
                        <>
                            <p className={styles.product_feature}>
                                <span className={styles.feature_title}>{t('product_features.wired')}</span>
                                <span className={styles.feature_value}>
                                    {product.wired === 0 ? t('product_features.wireless') : t('product_features.wired')}
                                </span>
                            </p>
                            <p className={styles.product_feature}>
                                <span className={styles.feature_title}>{t('product_features.wire_length')}</span>
                                <span className={styles.feature_value}>{product.length || '—'}</span>
                            </p>
                        </>
                    )}
                    {category_name === 'airbag_cables' && (
                        <>
                            <p className={styles.product_feature}>
                                <span className={styles.feature_title}>{t('product_features.steering_axle_bore_diameter')}</span>
                                <span className={styles.feature_value}>{product.steering_axle_bore_diameter}</span>
                            </p>
                            <p className={styles.product_feature}>
                                <span className={styles.feature_title}>{t('product_features.airbag_cable_plugs_number')}</span>
                                <span className={styles.feature_value}>{product.airbag_plugs_number}</span>
                            </p>
                        </>
                    )}
                    {(category_name === 'camshaft_sensors' || category_name === 'crankshaft_sensors') && (
                        <>
                            {product.sensor_type && (
                                <p className={styles.product_feature}>
                                    <span className={styles.feature_title}>{t('product_features.sensor_type')}</span>
                                    <span className={styles.feature_value}>{translatedProps.sensor_type || product.sensor_type}</span>
                                </p>
                            )}
                            <p className={styles.product_feature}>
                                <span className={styles.feature_title}>{t('product_features.wired')}</span>
                                <span className={styles.feature_value}>
                                    {product.wired === 0 ? t('product_features.wireless') : t('product_features.wired')}
                                </span>
                            </p>
                            <p className={styles.product_feature}>
                                <span className={styles.feature_title}>{t('product_features.contacts_number')}</span>
                                <span className={styles.feature_value}>{product.contact_number}</span>
                            </p>
                            <p className={styles.product_feature}>
                                <span className={styles.feature_title}>{t('product_features.connection_type')}</span>
                                <span className={styles.feature_value}>{translatedProps.connection_type}</span>
                            </p>
                        </>
                    )}
                    {category_name === 'spark_plugs' && (
                        <>
                            <p className={styles.product_feature}>
                                <span className={styles.feature_title}>{t('product_features.electrode_type')}</span>
                                <span className={styles.feature_value}>{translatedProps.electrode_type}</span>
                            </p>
                            <p className={styles.product_feature}>
                                <span className={styles.feature_title}>{t('product_features.electrodes_number')}</span>
                                <span className={styles.feature_value}>{product.electrodes_number}</span>
                            </p>
                            <p className={styles.product_feature}>
                                <span className={styles.feature_title}>{t('product_features.heat_rating')}</span>
                                <span className={styles.feature_value}>{product.heat_rating || '—'}</span>
                            </p>
                            <p className={styles.product_feature}>
                                <span className={styles.feature_title}>{t('product_features.key_type')}</span>
                                <span className={styles.feature_value}>{translatedProps.key_type}</span>
                            </p>
                            <p className={styles.product_feature}>
                                <span className={styles.feature_title}>{t('product_features.key_size')}</span>
                                <span className={styles.feature_value}>{product.key_size}</span>
                            </p>
                            <p className={styles.product_feature}>
                                <span className={styles.feature_title}>{t('product_features.seat_type')}</span>
                                <span className={styles.feature_value}>{translatedProps.seat_type}</span>
                            </p>
                            <p className={styles.product_feature}>
                                <span className={styles.feature_title}>{t('product_features.thread_size')}</span>
                                <span className={styles.feature_value}>{product.thread_size}</span>
                            </p>
                            <p className={styles.product_feature}>
                                <span className={styles.feature_title}>{t('product_features.thread_length')}</span>
                                <span className={styles.feature_value}>{product.thread_length}</span>
                            </p>
                            <p className={styles.product_feature}>
                                <span className={styles.feature_title}>{t('product_features.gap')}</span>
                                <span className={styles.feature_value}>{product.gap}</span>
                            </p>
                        </>
                    )}
                </div>
                {(!!product.count_original || !!product.count_copy) && <div className={styles.cart_container}>
                    <div className={styles.price_original_container}>
                        <div className={styles.price}>
                            <span className={styles.price_original}>
                                {discountedOriginalPrice}
                                <TbCurrencyDram/>
                            </span>
                            <div className={styles.price_name}>
                                <span>{t('product.original')}</span>
                                {!!product.count_copy && <input
                                    type="radio"
                                    checked={productType === 'original'}
                                    name="price_type"
                                    value="copy"
                                    onChange={() => setProductType('original')}
                                />}
                            </div>
                        </div>
                        {discountedOriginalPrice !== product.price_original && (
                            <>
                                <span className={styles.discount}>
                                    -{product.discount}%
                                </span>
                                <span className={styles.discounted_price}>
                                    {product.price_original}
                                    <TbCurrencyDram/>
                                </span>
                            </>
                        )}
                    </div>
                    {!!product.count_copy && <div className={styles.price_copy_container}>
                        <div className={styles.price}>
                            <span className={styles.price_copy}>
                                {discountedCopyPrice}
                                <TbCurrencyDram/>
                            </span>
                            <div className={styles.price_name}>
                                <span>{t('product.copy')}</span>
                                {<input
                                    type="radio"
                                    checked={productType === 'copy'}
                                    value="copy"
                                    onChange={() => setProductType('copy')}
                                    name="price_type"
                                />}
                            </div>
                        </div>
                        {discountedCopyPrice !== product.price_copy && (
                            <>
                                <span className={styles.discount}>
                                    -{product.discount}%
                                </span>
                                <span className={styles.discounted_price}>
                                    {product.price_copy}
                                    <TbCurrencyDram/>
                                </span>
                            </>
                        )}
                    </div>}
                    <CartActions product={product} productType={productType}/>
                </div>}
            </div>
            {category === 'spark_plugs' && (
                <HeatRangeChart />
            )}
            {!!Object.keys(refs).length && (
                <div className={styles.ref_container}>
                    <button className={styles.section_toggle} onClick={() => setRefsOpen(o => !o)}>
                        <span className={styles.ref_num}>{t('product_features.reference_number')}</span>
                        {refsOpen ? <FaChevronUp /> : <FaChevronDown />}
                    </button>
                    {refsOpen && (
                        <div className={styles.refs}>
                            {Object.keys(refs).sort().map((brand) => (
                                <div className={styles.ref_item} key={brand}>
                                    <span className={styles.ref_key}>{brand}</span>
                                    <div className={styles.ref_chips}>
                                        {refs[brand].map((num, i) => (
                                            <span className={styles.ref_value} key={`${num}-${i}`}>{num}</span>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
            {!!Object.keys(oems).length && (
                <div className={styles.oem_section}>
                    <button className={styles.section_toggle} onClick={() => setOemsOpen(o => !o)}>
                        <span className={styles.ref_num}>{t('product_features.oem')}</span>
                        {oemsOpen ? <FaChevronUp /> : <FaChevronDown />}
                    </button>
                    {oemsOpen && (
                        <div className={styles.oem_list}>
                            {Object.keys(oems).sort().map((model) => (
                                <div className={styles.oem_group} key={model}>
                                    <p className={styles.oem_group_label}>{model}</p>
                                    <div className={styles.oem_chips}>
                                        {oems[model].map((num, i) => (
                                            <span className={styles.oem_chip} key={`${num}-${i}`}>{num}</span>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            <CrossReference productId={product.product_id} />
        </div>
    ) : (
        <h3>{t('product_features.no_product')}</h3>
    )
}

export default Product
